import axios from 'axios';
import type {
  SocialUser,
  Preference,
  SocialMedias,
  MinifiedSocialUser,
  PublicSocialUser,
  ContactForm,
  CreateSocialUserRequest,
  ActiveTermsResponse,
  PublicCountry,
  DetectedGeo,
  LegalDocument,
} from './social.service.types';
import { useAuthStore } from '@/stores';

const backendUrl = import.meta.env.VITE_SOCIAL_URL;

const commonHeaders = {
  'Content-Type': 'application/json',
};

const socialApi = axios.create({
  baseURL: backendUrl,
  withCredentials: true,
  headers: { ...commonHeaders },
});

export const USERNAME_LENGTH = {
  MIN: 4,
  MAX: 16,
} as const;

export const socialService = {
  contact(data: ContactForm) {
    return socialApi.post<ContactForm>(`public/contact`, data);
  },
  getPublicCountries() {
    return socialApi.get<PublicCountry[]>(`public/countries`);
  },
  getDetectedCountry() {
    return socialApi.get<DetectedGeo>(`public/geo`);
  },
  getActiveTerms() {
    const authStore = useAuthStore();
    if (!authStore.user?.id) {
      return Promise.reject(new Error('User not authenticated'));
    }
    return socialApi.get<ActiveTermsResponse>(`terms/active`);
  },
  // Authenticated Privacy Policy, resolved by the user's assigned country. Informational
  // only — privacy has no acceptance flow, so there is no `userMustReAccept`.
  getActivePrivacy() {
    const authStore = useAuthStore();
    if (!authStore.user?.id) {
      return Promise.reject(new Error('User not authenticated'));
    }
    return socialApi.get<LegalDocument>(`privacy/active`);
  },
  // Public, country-aware legal documents for the footer pages of logged-out viewers.
  // Resolved server-side by the infra-injected `x-geo-country` header.
  getPublicActiveTerms() {
    return socialApi.get<LegalDocument>(`public/terms/active`);
  },
  getPublicActivePrivacy() {
    return socialApi.get<LegalDocument>(`public/privacy/active`);
  },
  updateSocialUser(data: Partial<SocialUser>) {
    return socialApi.put<SocialUser>(`socialUsers/update`, {
      data: data,
    });
  },
  createSocialUser(data: CreateSocialUserRequest) {
    return socialApi.post<SocialUser>(`/socialUsers/create`, data);
  },
  generateRandomUsername() {
    return socialApi.get<{ nickname: string }>(`socialUsers/newNickname`);
  },
  isValidUsername(username: string) {
    return socialApi.post<{ valid: boolean }>(`socialUsers/validUsername`, {
      username: username,
    });
  },
  getUsersById(ids: string[]) {
    return socialApi.post<MinifiedSocialUser[]>(`search/byIds`, { ids: ids });
  },
  getUsersByIdPublic(ids: string[]) {
    return socialApi.post<PublicSocialUser[]>(`/public/search/byIds`, {
      ids: ids,
    });
  },
  getMyUser() {
    const authStore = useAuthStore();
    if (!authStore.currentPlayer?.playerId) {
      return Promise.reject(new Error('User not authenticated'));
    }

    return socialApi.get<SocialUser>(`socialUsers/myUserDetails`);
  },
  getPreferences() {
    const authStore = useAuthStore();
    if (!authStore.user?.id) {
      return Promise.reject(new Error('User not authenticated'));
    }
    return socialApi.get<Preference[]>(`/preferences`);
  },
  uploadUserImage(userImg: string) {
    return socialApi.post<SocialUser>(`socialUsers/setUserImg`, {
      userImg,
    });
  },
  saveAvatar(RPMAvatarId: string) {
    return socialApi.post<SocialUser>(`/socialUsers/readyPlayerMe/saveAvatar`, {
      RPMAvatarId: RPMAvatarId,
    });
  },
  postAvatarUserId() {
    return socialApi.post<string>(`/socialUsers/readyPlayerMe/saveUserId`);
  },
  readyPlayerMeIframeToken() {
    return socialApi.get<{ subdomain: string; token: string }>(
      `/socialUsers/readyPlayerMe/iframeToken`,
    );
  },
  addPreferences(preferencesIds: string[]) {
    return socialApi.put<SocialUser>(`/socialUsers/addPreferences`, {
      preferencesId: preferencesIds,
    });
  },
  removePreferences(preferencesIds: string[]) {
    return socialApi.put<SocialUser>(`/socialUsers/removePreferences`, {
      preferencesId: preferencesIds,
    });
  },
  uploadSocialMedias(socialMedias: SocialMedias) {
    return socialApi.post('/socialUsers/uploadSocialMedias', {
      socialMedias: socialMedias,
    });
  },
};
